import { useCallback, useEffect, useReducer, useRef } from "react"
import { useAuth } from "@/context/AuthContext"
import {
  DeckBattleResumePayload,
  DeckBattleStartedPayload,
} from "@/types/battle"
import {
  connectBattleSocket,
  consumePendingDeckBattleResume,
  consumePendingDeckBattleStarted,
  clearPendingDeckBattleResume,
  clearPendingDeckBattleStarted,
  emitBattleEventWhenReady,
  onBattleEvent,
  peekPendingDeckBattleResume,
  requestDeckBattleRestore,
  waitForBattlePresenceReady,
} from "@/socket/battleSocket"
import {
  normalizeDeckBattleCancelledPayload,
  normalizeDeckBattleResumePayload,
  normalizeDeckBattleStartedPayload,
  normalizeDeckMatchResultPayload,
  normalizeDeckOpponentCardReadyPayload,
  normalizeDeckOpponentDisconnectedPayload,
  normalizeDeckOpponentReconnectedPayload,
  normalizeDeckPlayCardWaitingPayload,
  normalizeDeckRoundResultPayload,
} from "@/utils/deckPayload"
import { enrichBattleCardsShardsFromSource } from "@/utils/battleCards"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import {
  deckDuelReducer,
  getDeckAvailableCards,
  initialDeckDuelState,
  isYourTurnToPlay,
  isDeckInputLocked,
} from "./deckDuelReducer"
import { setActiveDeckBattle } from "@/features/deck/deckBattleSession"

function isBenignMissingBattleError(message: string | undefined): boolean {
  if (!message) return false
  const lower = message.toLowerCase()
  return (
    lower.includes("бой не найден") ||
    lower.includes("battle not found") ||
    lower.includes("no active battle") ||
    (lower.includes("активн") && lower.includes("не найден"))
  )
}

export function useDeckDuelMachine(initialBattle?: DeckBattleStartedPayload) {
  const { token } = useAuth()
  const { collectionCards } = useGuardiansPassport()
  const collectionRef = useRef(collectionCards)
  collectionRef.current = collectionCards
  const [state, dispatch] = useReducer(deckDuelReducer, initialDeckDuelState)
  const battleRef = useRef(state.battle)
  battleRef.current = state.battle
  const stateRef = useRef(state)
  stateRef.current = state
  const activeBattleIdRef = useRef<string | null>(null)
  const resumeInFlightRef = useRef(false)
  const playInFlightRef = useRef(false)
  const lastResumeAtRef = useRef(0)

  const isRelevantBattle = useCallback((battleId: string | undefined) => {
    if (!battleId) return false
    const current = activeBattleIdRef.current ?? battleRef.current?.battleId
    return !!current && current === battleId
  }, [])

  const applyResume = useCallback((raw: unknown) => {
    const payload = normalizeDeckBattleResumePayload(raw)
    if (!payload) return false

    const current = stateRef.current
    if (
      current.uiPhase === "match_end" ||
      current.uiPhase === "cancelled" ||
      current.matchResult
    ) {
      return false
    }

    const currentId = activeBattleIdRef.current ?? battleRef.current?.battleId
    if (currentId && currentId !== payload.battleId) return false

    const enrichedDeck = enrichBattleCardsShardsFromSource(
      payload.yourDeck,
      collectionRef.current
    )
    const enrichedRemaining = enrichBattleCardsShardsFromSource(
      payload.yourRemainingDeck,
      collectionRef.current
    )
    // Рука после resume строится из remaining; yourDeck — полный стартовый набор.
    const deckById = new Map(enrichedDeck.map((c) => [c.id, c]))
    for (const card of enrichedRemaining) {
      if (!deckById.has(card.id)) deckById.set(card.id, card)
    }
    const mergedDeck = Array.from(deckById.values())
    const enriched: DeckBattleResumePayload = {
      ...payload,
      yourDeck: mergedDeck.length > 0 ? mergedDeck : enrichedDeck,
      yourRemainingDeck: enrichedRemaining,
    }

    activeBattleIdRef.current = enriched.battleId
    setActiveDeckBattle({
      battleId: enriched.battleId,
      mode: enriched.mode,
      deckSearchMode: enriched.deckSearchMode,
      colorRank: enriched.colorRank,
      you: enriched.you,
      opponent: enriched.opponent,
      yourDeck: enriched.yourDeck,
      phase: enriched.phase,
      currentRound: enriched.currentRound,
      yourRoundWins: enriched.yourRoundWins,
      opponentRoundWins: enriched.opponentRoundWins,
      roundsToWin: enriched.roundsToWin,
      playCardTimeoutSec: enriched.playCardTimeoutSec,
    })
    lastResumeAtRef.current = Date.now()
    dispatch({ type: "DECK_BATTLE_RESUME", payload: enriched })
    return true
  }, [])

  const startBattle = useCallback((raw: unknown) => {
    const payload = normalizeDeckBattleStartedPayload(raw)
    if (!payload) return

    const current = stateRef.current
    // На экране победы/отмены не стартуем новый бой автоматически.
    if (
      current.uiPhase === "match_end" ||
      current.uiPhase === "cancelled" ||
      current.matchResult
    ) {
      if (current.battle?.battleId === payload.battleId) return
      return
    }

    const enriched: DeckBattleStartedPayload = {
      ...payload,
      yourDeck: enrichBattleCardsShardsFromSource(
        payload.yourDeck,
        collectionRef.current
      ),
    }
    if (
      activeBattleIdRef.current === enriched.battleId &&
      battleRef.current?.battleId === enriched.battleId
    ) {
      dispatch({ type: "DECK_BATTLE_SYNC", payload: enriched })
      return
    }
    activeBattleIdRef.current = enriched.battleId
    setActiveDeckBattle(enriched)
    dispatch({ type: "DECK_BATTLE_STARTED", payload: enriched })
  }, [])

  const reemitPlayedCard = useCallback(async () => {
    const s = stateRef.current
    const battle = s.battle
    const card = s.yourPlayedCard
    if (!battle || !card || !token) return false
    if (
      s.uiPhase !== "play_cards_waiting" &&
      s.uiPhase !== "play_cards"
    ) {
      return false
    }
    return emitBattleEventWhenReady(token, "deck-play-card", {
      battleId: battle.battleId,
      cardId: card.id,
    })
  }, [token])

  const restoreBattleFromServer = useCallback(async () => {
    const pending = peekPendingDeckBattleResume()
    if (pending && applyResume(pending)) {
      consumePendingDeckBattleResume()
      return true
    }

    const ack = await requestDeckBattleRestore(token)
    if (!ack) return false
    if (!ack.battle) return false
    return applyResume(ack.battle)
  }, [applyResume, token])

  useEffect(() => {
    if (initialBattle) {
      startBattle(initialBattle)
    }
  }, [initialBattle, startBattle])

  useEffect(() => {
    const cleanups = [
      onBattleEvent("deck-battle-started", (payload) => {
        startBattle(payload)
      }),
      onBattleEvent("deck-battle-resume", (payload) => {
        applyResume(payload)
      }),
      onBattleEvent("deck-play-card-waiting", (payload) => {
        const normalized = normalizeDeckPlayCardWaitingPayload(payload)
        if (!normalized || !isRelevantBattle(normalized.battleId)) return
        dispatch({ type: "PLAY_CARD_WAITING" })
      }),
      onBattleEvent("deck-opponent-card-ready", (payload) => {
        const normalized = normalizeDeckOpponentCardReadyPayload(payload)
        if (!normalized || !isRelevantBattle(normalized.battleId)) return
        dispatch({ type: "OPPONENT_CARD_READY" })
      }),
      onBattleEvent("deck-opponent-disconnected", (payload) => {
        const normalized = normalizeDeckOpponentDisconnectedPayload(payload)
        if (!normalized || !isRelevantBattle(normalized.battleId)) return
        dispatch({
          type: "OPPONENT_DISCONNECTED",
          payload: { graceSec: normalized.graceSec },
        })
      }),
      onBattleEvent("deck-opponent-reconnected", (payload) => {
        const normalized = normalizeDeckOpponentReconnectedPayload(payload)
        if (!normalized || !isRelevantBattle(normalized.battleId)) return
        dispatch({ type: "OPPONENT_RECONNECTED" })
      }),
      onBattleEvent("deck-round-result", (payload) => {
        const normalized = normalizeDeckRoundResultPayload(payload)
        if (!normalized || !isRelevantBattle(normalized.battleId)) return
        const youUserId = battleRef.current?.you.userId
        if (!youUserId) return
        dispatch({
          type: "ROUND_RESULT",
          payload: normalized,
          youUserId,
        })
      }),
      onBattleEvent("deck-match-result", (payload) => {
        const normalized = normalizeDeckMatchResultPayload(payload)
        if (!normalized || !isRelevantBattle(normalized.battleId)) return
        dispatch({ type: "MATCH_RESULT", payload: normalized })
        // Не даём late resume/started переоткрыть уже сыгранный матч.
        clearPendingDeckBattleResume()
        clearPendingDeckBattleStarted()
      }),
      onBattleEvent("deck-battle-cancelled", (payload) => {
        const normalized = normalizeDeckBattleCancelledPayload(payload)
        if (!normalized || !isRelevantBattle(normalized.battleId)) return
        dispatch({ type: "BATTLE_CANCELLED", payload: normalized })
      }),
      onBattleEvent("error", (p) => {
        if (!battleRef.current) return
        // После сворачивания «Бой не найден» не фатален — ждём resume/restore.
        if (
          stateRef.current.socketReconnecting &&
          isBenignMissingBattleError(p.message)
        ) {
          return
        }
        if (
          Date.now() - lastResumeAtRef.current < 4_000 &&
          isBenignMissingBattleError(p.message)
        ) {
          return
        }
        console.error("[deck-duel]", p.message)
      }),
    ]

    void connectBattleSocket(token)
      .then(async () => {
        const pendingStarted = consumePendingDeckBattleStarted()
        if (pendingStarted && !battleRef.current) {
          startBattle(pendingStarted)
        }
        const pendingResume = consumePendingDeckBattleResume()
        if (pendingResume) {
          applyResume(pendingResume)
        }
      })
      .catch(() => null)

    return () => {
      cleanups.forEach((fn) => fn())
    }
  }, [token, startBattle, applyResume, isRelevantBattle])

  useEffect(() => {
    const timerActive =
      state.uiPhase === "play_cards" || state.uiPhase === "play_cards_waiting"
    if (!timerActive || !state.deadlineAt) return

    const interval = setInterval(() => {
      dispatch({ type: "TICK_TIMER" })
    }, 1000)
    return () => clearInterval(interval)
  }, [state.uiPhase, state.deadlineAt])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return
      if (!battleRef.current || !token) return
      const phase = stateRef.current.uiPhase
      if (
        phase === "match_end" ||
        phase === "cancelled" ||
        stateRef.current.matchResult
      ) {
        return
      }
      if (resumeInFlightRef.current) return
      resumeInFlightRef.current = true
      dispatch({ type: "SOCKET_RECONNECTING" })

      void (async () => {
        try {
          // GuardiansOnline уже делает reconnectBattleSocket; здесь ждём presence
          // и снимок боя (deck-battle-resume или ручной restore).
          await connectBattleSocket(token)
          const ready = await waitForBattlePresenceReady(12_000)
          if (!ready) return

          // Авто-resume с бэка после connected — даём окно.
          await new Promise((resolve) => setTimeout(resolve, 600))
          if (Date.now() - lastResumeAtRef.current < 2_000) {
            await reemitPlayedCard()
            return
          }

          const restored = await restoreBattleFromServer()
          if (restored) {
            const s = stateRef.current
            // Если локально карта уже выбрана — после resume убеждаемся, что сервер её знает.
            if (s.yourPlayedCard) {
              await reemitPlayedCard()
            }
          }
        } catch {
          // сокет/presence недоступны — остаёмся на экране до cancelled
        } finally {
          dispatch({ type: "SOCKET_RECONNECTED" })
          resumeInFlightRef.current = false
        }
      })()
    }

    document.addEventListener("visibilitychange", onVisible)
    return () => document.removeEventListener("visibilitychange", onVisible)
  }, [token, reemitPlayedCard, restoreBattleFromServer])

  const playCard = useCallback(
    (cardId: string, abilityId?: string | null) => {
      const battle = state.battle
      if (!battle) return
      if (!isYourTurnToPlay(state) || isDeckInputLocked(state)) return
      if (playInFlightRef.current) return
      if (state.socketReconnecting) return

      const card = getDeckAvailableCards(state).find((c) => c.id === cardId)
      if (!card) return

      playInFlightRef.current = true
      dispatch({ type: "PLAY_CARD", payload: { card } })

      void (async () => {
        try {
          const ok = await emitBattleEventWhenReady(token, "deck-play-card", {
            battleId: battle.battleId,
            cardId,
            ...(abilityId ? { abilityId } : {}),
          })
          if (!ok) {
            dispatch({ type: "PLAY_CARD_FAILED" })
            console.warn(
              "Не удалось отправить карту. Проверьте сеть и выберите снова."
            )
          }
        } catch {
          dispatch({ type: "PLAY_CARD_FAILED" })
          console.warn(
            "Не удалось отправить карту. Проверьте сеть и выберите снова."
          )
        } finally {
          playInFlightRef.current = false
        }
      })()
    },
    [state, token]
  )

  const roundRevealDone = useCallback(() => {
    dispatch({ type: "ROUND_REVEAL_DONE" })
  }, [])

  const reset = useCallback(() => {
    activeBattleIdRef.current = null
    setActiveDeckBattle(null)
    clearPendingDeckBattleResume()
    clearPendingDeckBattleStarted()
    dispatch({ type: "RESET" })
  }, [])

  return {
    state,
    playCard,
    roundRevealDone,
    reset,
    availableCards: getDeckAvailableCards(state),
  }
}
